// ✨ Carousel.tsx — FINAL VERSION with UC frame time preserved
import "./carousel.css";
import { useRef, useState, useMemo, useEffect } from "react";
import { Box, Grid2, useTheme } from "@mui/material";
import ImageTag from "./ImageTag";
import PhotoSphere from "./PhotoSphere";
import MainImage from "./MainImage";
import PlayButton from "./PlayButton";
import ImageTypeSelect from "./ImageTypeSelect";
import ThumbnailSlider from "./ThumbnailSlider";
import { useSwipeable } from "react-swipeable";
import VideoFrameScrubber from "./VideoFrameScrubber";
import CustomImageViewer from "./CustomImageViewer";
import {
  buildSortedImages,
  getUniqueImageTypes,
  isWebGLSupported,
  isIframe,
  IResultSet,
} from "./carouselHelpers";
import CopyIframeUrlButton from "./CopyIframeUrlButton";


interface CarouselProps {
  images: any[];
}

const Carousel: React.FC<CarouselProps> = ({ images }) => {
  const theme = useTheme();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<IResultSet | null>(null);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<string>("all");
  const thumbnailRefs = useRef<(HTMLImageElement | null)[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUcViewerActive, setIsUcViewerActive] = useState(false);
  const [ucTime, setUcTime] = useState(0)
  const [ucImageSrc, setUcImageSrc] = useState("");

  const isIframeMode = useMemo(() => isIframe(), []);
  const isWebGL = useMemo(() => isWebGLSupported(), []);
  const sortedImages = useMemo(() => buildSortedImages(images), [images]);
  const uniqueImageTypes = useMemo(() => getUniqueImageTypes(sortedImages), [sortedImages]);


  const filteredImages = useMemo(() => {
    if (selectedType === "all") return sortedImages;
    return sortedImages.filter((image) => image.imageType === selectedType);
  }, [selectedType, sortedImages]);

  const handlers = useSwipeable({
    onSwipedLeft: () => handleGoTo((selectedImageIndex + 1) % filteredImages.length),
    onSwipedUp: () => handleGoTo((selectedImageIndex + 1) % filteredImages.length),
    onSwipedRight: () => handleGoTo((selectedImageIndex - 1 + filteredImages.length) % filteredImages.length),
    onSwipedDown: () => handleGoTo((selectedImageIndex - 1 + filteredImages.length) % filteredImages.length),
    trackMouse: true,
  });

  const handleGoTo = (idx: number) => {
    setSelectedImageIndex(idx);
    setSelectedImage(filteredImages[idx]);
    setIsPlaying(false);
    setIsUcViewerActive(false);
    setUcImageSrc(""); // 🧼 reset displayed frame image
    requestAnimationFrame(() => {
      const thumb = thumbnailRefs.current[idx];
      if (thumb) {
        thumb.scrollIntoView({ inline: 'center', block: 'nearest' });
      }
    });
  };

  const handleTogglePlay = () => setIsPlaying(prev => !prev);

  const handleImageClick = (image: IResultSet) => {
    if (image.imageType === "img_pano" && isWebGL) return;
    setSelectedImage(image);
    setViewerVisible(true);

    const isUC = ["img_uc_dewarp", "img_uc"].includes(image?.imageType || '');
    setIsUcViewerActive(isUC);
    if (!isUC) {
      setUcImageSrc("");
    }

    if (isIframeMode) {
      requestAnimationFrame(() => {
        const el = document.getElementById("fullscreen-viewer");
        if (el && el.requestFullscreen) {
          el.requestFullscreen().catch(err => {
            console.warn("Fullscreen failed", err);
          });
        }
      });
    }
  };

  const handleCloseViewer = () => {
    setViewerVisible(false);
    setIsUcViewerActive(false);
  };

  useEffect(() => {
    if (!isPlaying || filteredImages.length <= 1) return;
    const interval = setInterval(() => {
      setSelectedImageIndex(prev => (prev + 1) % filteredImages.length);
    }, 500);
    return () => clearInterval(interval);
  }, [isPlaying, filteredImages.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!viewerVisible) return;
      if (e.key === 'ArrowRight') {
        const nextIndex = (selectedImageIndex + 1) % filteredImages.length;
        handleGoTo(nextIndex);
      } else if (e.key === 'ArrowLeft') {
        const prevIndex = (selectedImageIndex - 1 + filteredImages.length) % filteredImages.length;
        handleGoTo(prevIndex);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [viewerVisible, selectedImageIndex, filteredImages]);

  const showPlayButton = ((uniqueImageTypes.length === 1 && filteredImages[0]?.imageType !== "img_pano") ||
    (uniqueImageTypes.length > 1 && selectedType !== "img_pano"));

  const isUcOnly = ((uniqueImageTypes.length === 1 && ["img_uc_dewarp", "img_uc"].includes(filteredImages[0]?.imageType || '')) ||
    (uniqueImageTypes.length > 1 && ["img_uc_dewarp", "img_uc"].includes(selectedType)));

  const MainImageContainer = () => {
    const selectedImage = filteredImages[selectedImageIndex];

    if (isUcOnly) {
      return (
        <Grid2 size={{ xs: 12 }} sx={{ height: "100%", display: "flex", justifyContent: "center" }}>
          <VideoFrameScrubber
            filteredImages={filteredImages}
            initialTime={ucTime}
            onFrameClick={(dataUrl, time) => {
              setUcTime(time)
              setUcImageSrc(dataUrl);
              setViewerVisible(true);
              setIsUcViewerActive(true);
            }}
          />
        </Grid2>
      );
    }

    const isPano = selectedImage?.imageType === "img_pano";

    return (
      <Grid2 size={{ xs: 12 }} sx={{ height: "80%", display: "flex", justifyContent: "center" }}>
        <Box
          key={selectedImage.idx}
          {...(!isPano ? handlers : {})}
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            minHeight: "100%",
            touchAction: "pan-y"
          }}
          onClick={() => handleImageClick(selectedImage)}
        >
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              display: "flex",
              alignItems: "center",
              gap: 1,
              zIndex: 10
            }}
          >
            <ImageTag tag={selectedImage?.tag} />
            <CopyIframeUrlButton />
          </Box>

          <ImageTypeSelect selectedType={selectedType} setSelectedType={setSelectedType} uniqueImageTypes={uniqueImageTypes} sortedImages={sortedImages} showPlayButton={showPlayButton} />
          {showPlayButton && !isUcOnly && (
            <PlayButton sliderPlaying={isPlaying} handleTogglePlay={handleTogglePlay} />
          )}
          {isPano && isWebGL ? (
            <PhotoSphere image={selectedImage?.src} type="img_pano" />
          ) : (
            <MainImage image={selectedImage?.src} index={selectedImage.idx} />
          )}
          {isPano && !isWebGL && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                right: 8,
                backgroundColor: "#ff4d4f",
                color: "white",
                textAlign: "center",
                padding: "8px 16px",
                fontWeight: 400,
                borderRadius: "4px",
                marginBottom: "8px",
                opacity: "0.8"
              }}
            >
              WebGL is not supported in your browser.
            </Box>
          )}
        </Box>
      </Grid2>
    );
  };

  return (
    <Box sx={{
      width: "100%",
      height: "100%",
      minHeight: "400px",
      margin: "0 auto",
      position: "relative",
      backgroundColor: theme.palette.background.paper,
      p: "20px 10px",
      borderRadius: 2,
      overflow: 'hidden',
    }}>
      <Grid2 container justifyContent="center" sx={{ height: "100%" }}>
        {filteredImages.length > 0 && <MainImageContainer />}
        {!isUcOnly && (
          <ThumbnailSlider images={filteredImages} selectedImageIndex={selectedImageIndex} handleGoTo={handleGoTo} />
        )}
      </Grid2>
      {viewerVisible && (
        <CustomImageViewer
          open={viewerVisible}
          onClose={handleCloseViewer}
          src={isUcViewerActive ? ucImageSrc : selectedImage?.src || ""}
          alt="Zoomed Image"
          onPrev={() => {
            handleGoTo((selectedImageIndex - 1 + filteredImages.length) % filteredImages.length);
          }}
          onNext={() => {
            handleGoTo((selectedImageIndex + 1) % filteredImages.length);
          }}
          initialTime={ucTime}
        />
      )}
    </Box>
  );
};

export default Carousel;
